import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Label,
    LabelDetail,
    Icon,
    Popup,
    Card,
    CardContent,
    CardHeader,
    CardMeta,
    CardDescription,
    Image,
    PlaceholderParagraph,
    PlaceholderLine,
    PlaceholderHeader,
    Placeholder,
    Divider,
    Segment,
    Button,
} from "semantic-ui-react";
import Member from "../../models/Member";
import moment from "moment";

export default function MemberChip({
    member,
    hideMembership,
    hideProfileImage,
    disableLink,
    nameAppend,
    className,
}) {
    const membership = new Member(member).getMembershipStatusData();
    const membershipExpiry = moment.utc(membership.expiry).format("DD/MM/YYYY");
    const membershipColour = membership.status === "EXPIRED" ? "red" : "";
    const membershipColourChip =
        hideMembership === true ? "" : membershipColour;

    // State to store extra data from your endpoint and popup visibility.
    const [loadingMemberData, setLoadingMemberData] = useState(true);
    const [memberData, setMemberData] = useState({});
    const [loadingMemberNotes, setLoadingMemberNotes] = useState(true);
    const [memberNotes, setMemberNotes] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [popupState, setPopupState] = useState(false);
    const [cardMembership, setCardMembership] = useState(false);
    const [cardMembershipExpiry, setCardMembershipExpiry] = useState(false);
    const [cardMembershipExpiryColour, setCardMembershipExpiryColour] =
        useState("");

    // When the popup is visible and extra data hasn't been fetched yet, call the endpoint.
    useEffect(() => {
        if (showPopup && !popupState) {
            //Use this to prevent the showPopup from triggering too many times on hover
            setPopupState(true);

            getUser(member._id);
            getNotes(member._id);
        }
    }, [showPopup, cardMembership]);

    const getUser = (id) => {
        Member.show(id)
            .then((member) => {
                setMemberData(member);
                setCardMembership(new Member(member).getMembershipStatusData());
                setCardMembershipExpiry(
                    moment
                        .utc(
                            new Member(member).getMembershipStatusData().expiry,
                        )
                        .format("DD/MM/YYYY"),
                );
                setCardMembershipExpiryColour(
                    new Member(member).getMembershipStatusData().status ===
                        "EXPIRED"
                        ? "red"
                        : "",
                );
                setLoadingMemberData(false);
            })
            .catch(() => {});
    };

    const getNotes = (id) => {
        Member.getNotes(id)
            .then((notes) => {
                setMemberNotes(notes.filter((note) => note.pinned === true));
                setLoadingMemberNotes(false);
            })
            .catch(() => {});
    };

    // The MemberChip content is your existing Label.
    // If disableLink is true we render a "div" instead of a Link.
    const chipContent = (
        <Label
            image
            as={disableLink !== true ? Link : "div"}
            to={disableLink !== true ? `/members/${member._id}` : undefined}
            color={membershipColourChip}
            {...(className ? { className } : {})}
        >
            {member.profileImage && hideProfileImage !== true && (
                <img
                    src={new Member(member).profileImageUrl}
                    alt={member.name}
                />
            )}
            {member.name}
            {nameAppend && ` (${nameAppend})`}
            {member.hasPinnedNotes && (
                <Icon className='orange ml-1 mr-0' name='warning circle' />
            )}
            {membership && hideMembership !== true && (
                <LabelDetail title={`Expires: ${membershipExpiry}`}>
                    {membership.membership}
                </LabelDetail>
            )}
        </Label>
    );

    const renderMemberNotes = () => {
        return memberNotes.map((note, index) => {
            return (
                <Segment vertical key={index}>
                    <div className='col-sm-12 p-0'>
                        <span>
                            <b>{note.createdBy.name}</b>
                        </span>
                        <span className='ml-3'>
                            {moment(note.createdAt).fromNow()}
                        </span>
                    </div>
                    <p className='mt-3' style={{ whiteSpace: "pre-line" }}>
                        {note.note}
                    </p>
                </Segment>
            );
        });
    };

    const renderMemberData = () => {
        const memberEntity = new Member(memberData).primaryEntity;

        if (!loadingMemberData) {
            return (
                <>
                    {memberEntity?.entity?.name && (
                        <CardMeta>{memberEntity.entity.name}</CardMeta>
                    )}
                    {memberData.phoneNumber && (
                        <CardMeta>{memberData.phoneNumber}</CardMeta>
                    )}
                    {memberData.email && (
                        <CardMeta>{memberData.email}</CardMeta>
                    )}
                </>
            );
        }

        return (
            <Placeholder>
                <PlaceholderLine />
                <PlaceholderLine />
            </Placeholder>
        );
    };

    // The popup's content: a card showing similar info plus extra details.
    const cardContent = (
        <Card>
            <CardContent>
                {member.profileImage && hideProfileImage !== true && (
                    <Image
                        src={new Member(member).profileImageUrl}
                        floated='right'
                        size='tiny'
                        circular
                    />
                )}
                <CardHeader>{memberData.name}</CardHeader>
                {renderMemberData()}
                <div style={{ clear: "both" }}>
                    {!loadingMemberData && cardMembership ? (
                        <Label
                            color={cardMembershipExpiryColour}
                            className='mt-3'
                            image
                        >
                            {cardMembership.membership}
                            <LabelDetail>
                                Exp: {cardMembershipExpiry}
                            </LabelDetail>
                        </Label>
                    ) : (
                        <Placeholder>
                            <PlaceholderLine />
                        </Placeholder>
                    )}
                    {memberNotes.length > 0 ? (
                        <>
                            <Divider horizontal className='mt-3'>
                                Pinned note{memberNotes.length > 1 ? "s" : ""}
                            </Divider>
                            <CardDescription>
                                {renderMemberNotes()}
                            </CardDescription>
                            {/* Add other extra fields from extraData as needed */}
                        </>
                    ) : (
                        loadingMemberNotes ?? (
                            <Placeholder>
                                <PlaceholderParagraph>
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                </PlaceholderParagraph>
                            </Placeholder>
                        )
                    )}
                </div>
            </CardContent>
            <CardContent extra>
                <Link to={`/members/${member._id}`}>
                    <Button primary className='theme'>
                        View
                    </Button>
                </Link>
            </CardContent>
        </Card>
    );

    return (
        <Popup
            trigger={chipContent} // The member chip element.
            on='hover' // Show the popup on hover.
            hoverable // Keeps the popup open if the user hovers over it.
            mouseEnterDelay={500}
            mouseLeaveDelay={1000}
            position='top left' // Position the popup (card's top left aligns to the chip's top left).
            onOpen={() => setShowPopup(true)} // When popup opens, mark as visible to trigger the fetch.
            onClose={() => setShowPopup(false)} // When popup closes, reset the state.
            style={{ padding: 0 }}
            basic={true}
        >
            {cardContent}
        </Popup>
    );
}
